/**
 * Yuno checkout integration for VehHistory.
 *
 * Requires the following globals to be set in the Blade template before this script:
 *   - yunoPublicKey, yunoCountryCode
 *   - yunoSessionUrl, yunoSessionUpdateUrl
 *   - yunoPaymentUrl, yunoStatusUrl, yunoFinalizeUrl
 *   - saveScreenUrl
 *   - packets (array of packet objects)
 */

import { generateScreenShot, cleanupScreenShot } from './screenshot';
import { collectBrowserData, getAgreeData } from './client-data';
import { setCheckoutLocked } from './lock';
import { validateEmail } from './utils';
import { track, planType } from '../analytics';

(function () {
    'use strict';

    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------
    let yunoInstance = null;
    let secureFieldsInstance = null;
    let checkoutSessionId = null;
    let card3dsPollingActive = false;
    let card3dsSdkResult = null;
    function requestTimeout(ms) {
        return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
            ? AbortSignal.timeout(ms)
            : undefined;
    }

    // Shown when we stop waiting: the charge may still settle, so the customer must not be nudged into paying twice.
    const PAYMENT_UNCONFIRMED_MESSAGE = 'We could not confirm your payment. Please do not pay again, contact support and we will check the charge for you.';
    // Whether the Yuno secure fields have been rendered yet (deferred until sidebar is visible)
    let secureFieldsRendered = false;

    // Wallet (Google Pay / Apple Pay) state
    let walletInitialized = false;
    /** True only after SDK mount completed — blocks proxy clicks until ready */
    let walletSessionReady = false;
    /** Incremented on each remount — only the latest request may clear the loading state */
    let walletRemountRequestId = 0;
    let walletAvailabilityChecked = false;
    let pendingWalletChargeId = null;
    let pendingWalletResult = null;

    const cardFieldState = { pan: null, expiration: null, cvv: null };

    const browserInfo = {
        accept_header: 'text/html',
        accept_browser: navigator.userAgent,
        accept_content: 'text/html',
        browser_time_difference: String(new Date().getTimezoneOffset()),
        color_depth: String(screen.colorDepth),
        java_enabled: false,
        javascript_enabled: true,
        language: navigator.language || 'en',
        screen_height: String(screen.height),
        screen_width: String(screen.width),
        user_agent: navigator.userAgent,
    };

    // -------------------------------------------------------------------------
    // UI helpers
    // -------------------------------------------------------------------------
    function getCsrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content || '';
    }

    function getUtmParams() {
        let utmParams = localStorage.getItem('utm_params');
        if (utmParams === null) {
            utmParams = sessionStorage.getItem('utm_params');
        }
        try {
            JSON.parse(utmParams);
        } catch (e) {
            return null;
        }
        return utmParams;
    }

    // Safely parse JSON from a fetch response.
    // If the server returned HTML (e.g. error page or expired session),
    // throws a generic user-friendly error instead of a raw SyntaxError.
    async function safeParseJson(resp) {
        const text = await resp.text();
        try {
            return JSON.parse(text);
        } catch (e) {
            throw new Error('Payment could not be processed. Please try again.');
        }
    }

    function getEmailValue() {
        return (document.getElementById('email')?.value || '').trim();
    }

    function setCheckoutSessionInput(value) {
        const el = document.getElementById('checkout-session');
        if (el) el.value = value || '';
    }

    function getPacketId() {
        return document.getElementById('packet-id')?.value || '';
    }

    const PAYMENT_TYPES = { cc: 'card', pp: 'paypal', google: 'google_pay', apple: 'apple_pay' };

    function planParams(id) {
        const packet = (typeof packets !== 'undefined' && Array.isArray(packets))
            ? packets.find(function (p) { return String(p.id) === String(id); })
            : null;

        return {
            plan_id: String(id),
            plan_type: planType(packet),
            value: Number(getPacketAmount(id)) || undefined,
            currency: 'USD',
        };
    }

    function paymentParams(payType) {
        const type = PAYMENT_TYPES[payType] || 'card';

        return Object.assign(planParams(getPacketId()), {
            payment_type: type,
            payment_provider: type === 'paypal' ? 'paypal' : 'yuno',
        });
    }

    var payInPrevDisplay = '';

    function showPaymentError(message, pending) {
        // Pending is no failure: the payment may well have gone through.
        !pending && track('payment_error', Object.assign(paymentParams(document.getElementById('pay-type')?.value), {
            error_message: String(message || '').slice(0, 100),
        }));

        const payFail = document.getElementById('pay-fail');

        // Main precheck page — same behavior as old checkout
        if (payFail) {
            const msgEl = document.getElementById('pay-error-message');
            if (msgEl) msgEl.textContent = message || '';

            // Hide pay button and fade out form, fade in error block
            const payBtn = document.querySelector('#pay-block #pay-btn');
            if (payBtn) payBtn.style.display = 'none';

            const payIn = document.querySelector('.pay-in');
            if (payIn) {
                // Save the current computed display so we can restore it exactly
                payInPrevDisplay = getComputedStyle(payIn).display;
                payIn.style.transition = 'opacity 0.3s';
                payIn.style.opacity = '0';
                setTimeout(function () {
                    payIn.style.transition = '';
                    payIn.style.opacity = '';
                    payIn.style.display = 'none';
                    payFail.style.display = 'block';
                    payFail.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
            return;
        }

        // Sidebar pages — inline error
        const el = document.getElementById('payment-fail');
        if (el) {
            el.textContent = message || 'Payment failed. Please try again.';
            el.style.display = 'block';
            el.style.color = 'red';
            el.style.marginTop = '10px';
        }
    }

    function hidePaymentError() {
        // Main precheck page — only reset if error block is currently visible
        const payFail = document.getElementById('pay-fail');
        if (payFail && payFail.style.display !== 'none' && payFail.offsetParent !== null) {
            payFail.style.display = 'none';
            const payIn = document.querySelector('.pay-in');
            if (payIn) {
                payIn.style.display = payInPrevDisplay || 'flex';
            }
            return;
        }

        // Sidebar pages
        const el = document.getElementById('payment-fail');
        if (el) el.style.display = 'none';
    }

    // The spinner is the page lock: every payment shows it before anything is sent and every
    // failure path hides it, while a successful payment leaves it up until the redirect lands.
    function showSpinner() {
        setCheckoutLocked(true);
        document.querySelectorAll('.lds-facebook').forEach(function (el) {
            el.classList.remove('display-none');
        });
    }

    function hideSpinner() {
        setCheckoutLocked(false);
        document.querySelectorAll('.lds-facebook').forEach(function (el) {
            el.classList.add('display-none');
        });
    }

    function disablePayButton(disabled) {
        document.querySelectorAll('.pay-btn').forEach(function (btn) {
            btn.disabled = disabled;
            btn.classList.toggle('disabled', disabled);
        });
    }

    function showFieldError(fieldId, message) {
        const el = document.getElementById(fieldId + '-error');
        if (el) {
            el.textContent = message || 'Invalid field';
            el.classList.add('show');
        }
        const field = document.getElementById(fieldId);
        if (field) field.classList.add('is-invalid');
    }

    function clearFieldError(fieldId) {
        const el = document.getElementById(fieldId + '-error');
        if (el) {
            el.textContent = '';
            el.classList.remove('show');
        }
        const field = document.getElementById(fieldId);
        if (field) field.classList.remove('is-invalid');
    }

    function clearAllFieldErrors() {
        ['cc-number', 'cc-exp-date', 'cc-csc', 'cc-name'].forEach(clearFieldError);
    }

    // The server's own email check (a 422) answers under the field, like the inline one.
    function showServerEmailError(result) {
        const message = result.errors && result.errors.email && result.errors.email[0];
        if (!message) return false;
        showFieldError('email', message);
        scrollToFirstError();
        return true;
    }

    function scrollToFirstError() {
        var target = document.querySelector('.is-invalid, .yuno-secure-field.is-invalid, #payment-fail[style*="block"]');
        if (!target) {
            target = document.getElementById('card-data');
        }
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    function validateAgree() {
        let valid = true;

        const agree = document.getElementById('agree');
        const agreeFirst = document.getElementById('agreeFirst');
        const agreeSecond = document.getElementById('agreeSecond');

        if (agree && agree.offsetParent !== null && !agree.checked) {
            agree.classList.add('is-invalid');
            valid = false;
        }
        if (agreeFirst && agreeFirst.offsetParent !== null && !agreeFirst.checked) {
            agreeFirst.classList.add('is-invalid');
            valid = false;
        }
        if (agreeSecond && agreeSecond.offsetParent !== null && !agreeSecond.checked) {
            agreeSecond.classList.add('is-invalid');
            valid = false;
        }

        return valid;
    }

    function validateEmailField() {
        const email = getEmailValue();
        if (!email) {
            showFieldError('email', 'Email is required');
            return false;
        }
        if (!validateEmail(email)) {
            showFieldError('email', 'That doesn\'t look like a valid email. Please check for typos');
            return false;
        }
        clearFieldError('email');
        return true;
    }

    // -------------------------------------------------------------------------
    // ZIP / Postal code validation
    // -------------------------------------------------------------------------

    function validateCanadianPostalCode(value) {
        return /^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(value);
    }

    function validateUKPostalCode(value) {
        var patterns = [
            /^[A-Z]\d[A-Z] \d[A-Z][A-Z]$/,
            /^[A-Z]\d\d \d[A-Z][A-Z]$/,
            /^[A-Z][A-Z]\d \d[A-Z][A-Z]$/,
            /^[A-Z][A-Z]\d\d \d[A-Z][A-Z]$/,
        ];
        return patterns.some(function (p) { return p.test(value); });
    }

    function validateUSZipCode(value) {
        return /^\d{5}(-\d{4})?$/.test(value);
    }

    function handleTextSelectionKeys(event) {
        var allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (allowed.includes(event.key)) return true;
        if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z', 'y'].includes(event.key.toLowerCase())) return true;
        if (event.shiftKey && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return true;
        return false;
    }

    function allowOnlyDigits(event) {
        if (handleTextSelectionKeys(event)) return;
        if (!/^[0-9]$/.test(event.key)) event.preventDefault();
    }

    function cleanNonDigits(event) {
        var input = event.target;
        var cleaned = input.value.replace(/[^0-9]/g, '');
        if (input.value !== cleaned) input.value = cleaned;
        input.classList.remove('is-invalid');
    }

    function handleCanadianKeydown(event) {
        if (event.key === 'Backspace') {
            var input = event.target;
            var pos = input.selectionStart;
            if (pos > 0 && input.value.charAt(pos - 1) === ' ') {
                event.preventDefault();
                input.value = input.value.substring(0, pos - 2) + input.value.substring(pos);
                input.setSelectionRange(pos - 2, pos - 2);
                return;
            }
        }
        if (handleTextSelectionKeys(event)) return;
        if (!/^[A-Za-z0-9]$/.test(event.key)) event.preventDefault();
    }

    function handleUKKeydown(event) {
        if (event.key === 'Backspace') {
            var input = event.target;
            var pos = input.selectionStart;
            if (pos > 0 && input.value.charAt(pos - 1) === ' ') {
                event.preventDefault();
                input.value = input.value.substring(0, pos - 2) + input.value.substring(pos);
                input.setSelectionRange(pos - 2, pos - 2);
                return;
            }
        }
        if (handleTextSelectionKeys(event)) return;
        if (!/^[A-Za-z0-9]$/.test(event.key)) event.preventDefault();
    }

    function formatCanadianPostalCode(event) {
        var input = event.target;
        var value = input.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 6);
        if (value.length > 3) value = value.substring(0, 3) + ' ' + value.substring(3);
        input.value = value;
        input.classList.remove('is-invalid');
    }

    function formatUKPostalCode(event) {
        var input = event.target;
        var value = input.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 7);
        if (value.length >= 4) {
            var sp = value.length === 4 ? 1 : value.length === 5 ? 2 : value.length === 6 ? 3 : 4;
            value = value.substring(0, sp) + ' ' + value.substring(sp);
        }
        input.value = value;
        input.classList.remove('is-invalid');
    }

    function handleZipPaste(event, country) {
        event.preventDefault();
        var paste = (event.clipboardData || window.clipboardData).getData('text');
        var input = event.target;
        if (country === 'us') {
            input.value = paste.replace(/[^0-9]/g, '').substring(0, 5);
        } else if (country === 'ca') {
            var c = paste.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 6);
            input.value = c.length > 3 ? c.substring(0, 3) + ' ' + c.substring(3) : c;
        } else if (country === 'gb') {
            var g = paste.replace(/[^A-Za-z0-9]/g, '').toUpperCase().substring(0, 7);
            if (g.length >= 4) {
                var sp2 = g.length === 4 ? 1 : g.length === 5 ? 2 : g.length === 6 ? 3 : 4;
                input.value = g.substring(0, sp2) + ' ' + g.substring(sp2);
            } else {
                input.value = g;
            }
        }
        input.classList.remove('is-invalid');
    }

    function initializeZipCodeValidation(elementId) {
        var zipCode = document.getElementById(elementId);
        if (!zipCode) return;
        var country = zipCode.getAttribute('data-country');
        if (country === 'us') {
            zipCode.addEventListener('keydown', allowOnlyDigits);
            zipCode.addEventListener('input', cleanNonDigits);
            zipCode.addEventListener('paste', function (e) { handleZipPaste(e, country); });
        } else if (country === 'ca') {
            zipCode.addEventListener('input', formatCanadianPostalCode);
            zipCode.addEventListener('paste', function (e) { handleZipPaste(e, country); });
            zipCode.addEventListener('keydown', handleCanadianKeydown);
        } else if (country === 'gb') {
            zipCode.addEventListener('input', formatUKPostalCode);
            zipCode.addEventListener('paste', function (e) { handleZipPaste(e, country); });
            zipCode.addEventListener('keydown', handleUKKeydown);
        } else {
            zipCode.addEventListener('input', function (e) { e.target.classList.remove('is-invalid'); });
        }
    }

    function validateZipCode(elementId) {
        var zipField = document.getElementById(elementId);
        if (!zipField) return { isValid: true };

        var zipError = document.getElementById('cc-zip-error');
        var zipValue = zipField.value.trim();
        var zipRequired = zipField.getAttribute('data-zip-required') === 'true';
        var country = zipField.getAttribute('data-country');

        if (zipError) { zipError.textContent = ''; zipError.classList.remove('show'); }
        zipField.classList.remove('is-invalid');

        if (zipRequired && !zipValue) {
            var msg = (country === 'ca' || country === 'gb') ? 'Postal code is required' : 'ZIP code is required';
            if (zipError) { zipError.textContent = msg; zipError.classList.add('show'); }
            zipField.classList.add('is-invalid');
            return { isValid: false };
        }

        if (zipValue && zipValue.length > 16) {
            var msg2 = 'ZIP/postal code must not exceed 16 characters';
            if (zipError) { zipError.textContent = msg2; zipError.classList.add('show'); }
            zipField.classList.add('is-invalid');
            return { isValid: false };
        }

        if (zipValue && zipRequired) {
            var isValid = true;
            var errorMsg = '';
            if (country === 'us') { isValid = validateUSZipCode(zipValue); errorMsg = 'Please enter a valid ZIP code (e.g., 12345)'; }
            else if (country === 'ca') { isValid = validateCanadianPostalCode(zipValue); errorMsg = 'Please enter a valid postal code (e.g., M5T 1T4)'; }
            else if (country === 'gb') { isValid = validateUKPostalCode(zipValue); errorMsg = 'Please enter a valid postal code (e.g., WS11 1DB)'; }
            if (!isValid) {
                if (zipError) { zipError.textContent = errorMsg; zipError.classList.add('show'); }
                zipField.classList.add('is-invalid');
                return { isValid: false };
            }
        }

        return { isValid: true };
    }

    function validateForm() {
        let valid = true;

        if (!validateEmailField()) valid = false;

        const ccName = document.getElementById('cc-name');
        const ccNameValue = ccName ? ccName.value.trim() : '';
        if (!ccNameValue) {
            showFieldError('cc-name', 'Name on card is required');
            valid = false;
        } else if (ccNameValue.length < 3) {
            showFieldError('cc-name', 'Name on card must be at least 3 characters');
            valid = false;
        }

        if (cardFieldState.pan === false) {
            showFieldError('cc-number', 'Invalid card number');
            valid = false;
        } else if (cardFieldState.pan === null) {
            showFieldError('cc-number', 'Card number is required');
            valid = false;
        }

        if (cardFieldState.expiration === false) {
            showFieldError('cc-exp-date', 'Invalid expiration date');
            valid = false;
        } else if (cardFieldState.expiration === null) {
            showFieldError('cc-exp-date', 'Expiration date is required');
            valid = false;
        }

        if (cardFieldState.cvv === false) {
            showFieldError('cc-csc', 'Invalid CVV');
            valid = false;
        } else if (cardFieldState.cvv === null) {
            showFieldError('cc-csc', 'CVV is required');
            valid = false;
        }

        if (!validateZipCode('cc-zip').isValid) valid = false;

        if (!validateAgree()) valid = false;

        return valid;
    }

    function validatePaypalForm() {
        let valid = true;
        if (!validateEmailField()) valid = false;
        if (!validateAgree()) valid = false;
        return valid;
    }

    // -------------------------------------------------------------------------
    // Yuno Secure Fields iframe styles
    // -------------------------------------------------------------------------
    const FIELD_STYLES = [
        'div, fieldset, legend { border: none !important; box-shadow: none !important; outline: none !important; }',
        '[class*="fieldset__box"] { border: none !important; border-radius: 10px !important; padding: 0 !important; }',
        '[class*="input__content"] { padding: 0 !important; background: #fff !important; border-radius: 10px !important; border: none !important; height: 47px !important; }',
        '[class*="input__label"] { display: none !important; }',
        '[class*="error-text-field__content"] { display: none !important; }',
        '[class*="input__base"], input { background: transparent !important; font-size: 15px !important; padding: 0 12px !important; height: 47px !important; min-height: 0 !important; border: none !important; outline: none !important; color: #101828 !important; }',
    ].join('\n');

    // -------------------------------------------------------------------------
    // Session fetch
    // -------------------------------------------------------------------------
    async function fetchCheckoutSession(packetId) {
        const resp = await fetch(yunoSessionUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            body: JSON.stringify({ packet_id: packetId }),
        });

        if (!resp.ok) {
            throw new Error('Failed to create checkout session');
        }

        const data = await resp.json();
        if (data.error) {
            throw new Error(data.error);
        }

        return data.checkout_session;
    }

    // -------------------------------------------------------------------------
    // Secure Fields rendering
    // -------------------------------------------------------------------------
    function renderSecureFields(secureFields) {
        // Remove any stale iframes before re-rendering
        ['cc-number', 'cc-exp-date', 'cc-csc'].forEach(function (id) {
            const el = document.getElementById(id);
            if (el) {
                el.querySelectorAll('iframe').forEach(function (iframe) {
                    try { iframe.remove(); } catch (_) { /* already detached */ }
                });
            }
        });

        secureFields.create({
            name: 'pan',
            options: {
                placeholder: '1234 1234 1234 1234',
                designType: 'label-placeholder',
                styles: FIELD_STYLES,
                showError: true,
                onChange: function (data) {
                    cardFieldState.pan = !data.error;
                    if (!data.error) {
                        document.getElementById('cc-number')?.classList.remove('is-invalid');
                        clearFieldError('cc-number');
                    }
                },
                onFocus: function () {
                    document.getElementById('cc-number')?.classList.add('focused');
                },
                onBlur: function () {
                    document.getElementById('cc-number')?.classList.remove('focused');
                },
            },
        }).render('#cc-number');

        secureFields.create({
            name: 'expiration',
            options: {
                placeholder: 'MM / YY',
                designType: 'label-placeholder',
                styles: FIELD_STYLES,
                showError: true,
                onChange: function (data) {
                    cardFieldState.expiration = !data.error;
                    if (!data.error) {
                        document.getElementById('cc-exp-date')?.classList.remove('is-invalid');
                        clearFieldError('cc-exp-date');
                    }
                },
                onFocus: function () {
                    document.getElementById('cc-exp-date')?.classList.add('focused');
                },
                onBlur: function () {
                    document.getElementById('cc-exp-date')?.classList.remove('focused');
                },
            },
        }).render('#cc-exp-date');

        secureFields.create({
            name: 'cvv',
            options: {
                placeholder: 'CVV',
                designType: 'label-placeholder',
                styles: FIELD_STYLES,
                showError: true,
                onChange: function (data) {
                    cardFieldState.cvv = !data.error;
                    if (!data.error) {
                        document.getElementById('cc-csc')?.classList.remove('is-invalid');
                        clearFieldError('cc-csc');
                    }
                },
                onFocus: function () {
                    document.getElementById('cc-csc')?.classList.add('focused');
                },
                onBlur: function () {
                    document.getElementById('cc-csc')?.classList.remove('focused');
                },
            },
        }).render('#cc-csc');
    }

    // -------------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------------
    async function initYuno() {
        if (typeof yunoPublicKey === 'undefined' || !yunoPublicKey) {
            console.warn('[YUNO] Public key not configured');
            return;
        }

        try {
            const packetId = getPacketId();
            checkoutSessionId = await fetchCheckoutSession(packetId);
            setCheckoutSessionInput(checkoutSessionId);

            if (typeof Yuno === 'undefined') {
                console.error('[YUNO] Yuno SDK not loaded');
                return;
            }

            yunoInstance = await Yuno.initialize(yunoPublicKey);

            const secureFields = await yunoInstance.secureFields({
                countryCode: typeof yunoCountryCode !== 'undefined' ? yunoCountryCode : 'US',
                checkoutSession: checkoutSessionId,
            });

            secureFieldsInstance = secureFields;

            // Render fields immediately if the card container is already visible;
            // otherwise defer until the sidebar is shown (packets page hides pay-block initially).
            const payBlock = document.getElementById('pay-block');
            if (!payBlock || payBlock.style.display !== 'none' && getComputedStyle(payBlock).display !== 'none') {
                renderSecureFields(secureFields);
                secureFieldsRendered = true;
            }

            // Mounted up front so a wallet this device cannot use is hidden before its tab is offered.
            void initWalletButtons();
        } catch (err) {
            console.error('[YUNO] Initialization error:', err);
            showPaymentError('Payment form initialization failed. Please reload the page.');
        }
    }

    // Render secure fields the first time the sidebar becomes visible
    function maybeRenderSecureFields() {
        if (secureFieldsRendered || !secureFieldsInstance) return;
        try {
            renderSecureFields(secureFieldsInstance);
            secureFieldsRendered = true;
        } catch (err) {
            console.error('[YUNO] Deferred render error:', err);
        }
    }

    // -------------------------------------------------------------------------
    // PayPal payment — uses the legacy payUrl endpoint (pay-type=pp)
    // -------------------------------------------------------------------------
    function showDisputeModal() {
        const modal = document.getElementById('dispute-modal');
        if (modal && typeof $ !== 'undefined' && $.fn.modal) {
            $(modal).modal('show');
        }
    }

    function submitPaypalPayment() {
        if (window.onDh) {
            showDisputeModal();
            return;
        }

        const payUrl = window.payUrl;
        if (!payUrl) {
            showPaymentError('PayPal payment URL is not configured.');
            return;
        }

        disablePayButton(true);
        showSpinner();
        hidePaymentError();

        // Start screenshot capture in background while waiting for PayPal API
        generateScreenShot().catch(function () {});

        const form = document.getElementById('payment-form');
        const formData = form ? new FormData(form) : new FormData();

        formData.set('type', 'pp');
        formData.set('email', getEmailValue());

        const agree = document.getElementById('agree');
        const agreeFirst = document.getElementById('agreeFirst');
        const agreeSecond = document.getElementById('agreeSecond');

        if (agreeFirst && agreeSecond) {
            formData.set('agree-first', agreeFirst.checked ? 'on' : 'off');
            formData.set('agree-second', agreeSecond.checked ? 'on' : 'off');
            formData.set('agree', agreeFirst.checked ? 'on' : 'off');
        } else if (agree) {
            formData.set('agree', agree.checked ? 'on' : 'off');
        }

        formData.set('agree-data', JSON.stringify(getAgreeData()));
        formData.set('client-data', JSON.stringify(collectBrowserData()));

        const params = new URLSearchParams();
        formData.forEach(function (value, key) { params.append(key, value); });

        fetch(payUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            body: params.toString(),
        })
            .then(function (resp) { return resp.json(); })
            .then(async function (result) {
                if (result.redirect) {
                    await saveOrderData(result.id, 'pp');
                    window.location.href = result.redirect;
                } else if (!result.success) {
                    hideSpinner();
                    disablePayButton(false);
                    if (result.hasTrial) {
                        handleTrialError(result.message);
                    } else if (!showServerEmailError(result)) {
                        showPaymentError(result.message || 'PayPal payment failed. Please try again.');
                    }
                }
            })
            .catch(function () {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('PayPal payment failed. Please try again.');
            });
    }

    // -------------------------------------------------------------------------
    // Payment submission
    // -------------------------------------------------------------------------
    async function submitYunoPayment() {
        if (window.onDh) {
            showDisputeModal();
            return;
        }

        hidePaymentError();
        clearAllFieldErrors();

        if (!getPacketId()) {
            selectPacketAlert();
            return;
        }

        if (!validateForm()) {
            scrollToFirstError();
            return;
        }

        if (!secureFieldsInstance || !checkoutSessionId) {
            showPaymentError('Payment form is not ready. Please wait and try again.');
            return;
        }

        track('add_payment_info', paymentParams('cc'));
        disablePayButton(true);
        showSpinner();

        try {
            // Generate One-Time Token from Yuno Secure Fields
            const cardData = {
                cardHolderName: (document.getElementById('cc-name')?.value || '').trim(),
                zip: (document.getElementById('cc-zip')?.value || '').trim() || undefined,
            };

            const tokenResult = await secureFieldsInstance.generateTokenWithInformation({
                checkoutSession: checkoutSessionId,
                cardHolderName: cardData.cardHolderName,
            });

            console.log('[YUNO] generateTokenWithInformation result:', tokenResult);

            const oneTimeToken = tokenResult?.token;
            if (!oneTimeToken || typeof oneTimeToken !== 'string') {
                hideSpinner();
                disablePayButton(false);
                console.error('[YUNO] Invalid token result:', tokenResult);
                showPaymentError('Failed to generate payment token. Please check your card details.');
                scrollToFirstError();
                recreateCheckoutSession();
                return;
            }

            const packetId = getPacketId();
            const payload = {
                email: getEmailValue(),
                packet: packetId,
                token: oneTimeToken,
                checkout_session: checkoutSessionId,
                vin: document.getElementById('pay-vin')?.value || '',
                vehicle: document.getElementById('pay-vehicle')?.value || '',
                variant: document.querySelector('[name="variant"]')?.value || '',
                paymentType: 'card',
                browser_info: JSON.stringify(browserInfo),
                cardData: JSON.stringify(cardData),
                'page-uuid': document.getElementById('page-uuid')?.value || '',
                'client-data': JSON.stringify(collectBrowserData()),
                'agree-data': JSON.stringify(getAgreeData()),
                'cc-zip': (document.getElementById('cc-zip')?.value || '').trim(),
                'cc-country': document.getElementById('cc-country')?.value || '',
                utm_params: getUtmParams(),
            };

            // Start screenshot capture in background while waiting for payment API
            generateScreenShot().catch(function () {});

            const resp = await fetch(yunoPaymentUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                body: JSON.stringify(payload),
            });

            const result = await safeParseJson(resp);
            await handlePaymentResult(result, payload);
        } catch (err) {
            hideSpinner();
            disablePayButton(false);
            showPaymentError(err.message || 'Payment failed. Please try again.');
            console.error('[YUNO] Payment error:', err);
            recreateCheckoutSession();
        }
    }

    // -------------------------------------------------------------------------
    // Payment result handling
    // -------------------------------------------------------------------------
    async function handlePaymentResult(result, originalPayload) {
        if (!result.success) {
            hideSpinner();
            disablePayButton(false);
            if (result.hasTrial) {
                handleTrialError(result.message);
            } else if (!showServerEmailError(result)) {
                showPaymentError(result.message || 'Payment failed. Please try again.');
            }
            // Recreate session so the next attempt gets a fresh checkout_session
            recreateCheckoutSession();
            return;
        }

        // 3DS challenge required
        if (result.sdk_action_required && result.charge_id) {
            await handle3dsChallenge(result.charge_id, originalPayload);
            return;
        }

        // Success
        if (result.redirect) {
            await saveOrderData(result.id);
            window.location.href = result.redirect;
            return;
        }

        // Neither a redirect nor a challenge that can run (one without its charge id): nothing is
        // left to wait for, so the page must not stay locked.
        hideSpinner();
        disablePayButton(false);
        showPaymentError('Payment failed. Please try again.');
        recreateCheckoutSession();
    }

    async function handle3dsChallenge(chargeId, originalPayload) {
        card3dsPollingActive = true;

        try {
            if (yunoInstance && typeof yunoInstance.continuePayment === 'function') {
                try {
                    await yunoInstance.continuePayment({
                        checkoutSession: checkoutSessionId,
                        showPaymentStatus: false,
                        showLoading: false,
                        countryCode: (typeof yunoCountryCode !== 'undefined' ? yunoCountryCode : 'US'),
                        // Re-registered per call: the background startCheckout may have failed or been replaced by a session recreation.
                        yunoPaymentResult: onPaymentResult,
                    });
                } catch (err) {
                    // Swallow — errors are handled by polling the payment status below
                    console.warn('[YUNO] continuePayment error (swallowed):', err && err.message);
                }
            }

            // Allow any pending SDK callbacks (yunoError / yunoPaymentResult) to settle
            await new Promise(function (resolve) { setTimeout(resolve, 50); });

            // The challenge window may still be open: poll for up to ~5 min, an SDK result cuts it short.
            const status = await pollPaymentStatus(chargeId, 150, 2000);

            if (status === 'SUCCEEDED') {
                await finalizePayment(chargeId, originalPayload);
            } else if (status === 'DECLINED') {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('Payment was declined. Please try a different card.');
                recreateCheckoutSession();
            } else if (status === 'CANCELED') {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('Payment was cancelled. Please try again.');
                recreateCheckoutSession();
            } else if (status === 'TIMEOUT' || status === 'UNCONFIRMED') {
                // Pay buttons stay disabled: the charge can still settle at Yuno and a second attempt would double-charge.
                hideSpinner();
                showPaymentError(PAYMENT_UNCONFIRMED_MESSAGE, true);
            } else {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('Payment was cancelled or failed. Please try again.');
                recreateCheckoutSession();
            }
        } catch (err) {
            hideSpinner();
            disablePayButton(false);
            showPaymentError(err.message || 'Payment failed during 3DS. Please try again.');
            recreateCheckoutSession();
        } finally {
            card3dsPollingActive = false;
            card3dsSdkResult = null;
        }
    }

    // Wait up to `totalMs` ms but return early (within ~200ms) if card3dsSdkResult is set.
    async function sleepWatchingSdkSignal(totalMs) {
        const step = 200;
        const end = Date.now() + totalMs;
        while (Date.now() < end) {
            if (card3dsSdkResult) return;
            await sleep(Math.min(step, end - Date.now()));
        }
    }

    async function pollPaymentStatus(chargeId, maxAttempts, intervalMs) {
        maxAttempts = maxAttempts || 60;
        intervalMs = intervalMs || 3000;
        let transportFailures = 0;
        // Attempts alone drift with request latency; the customer-facing wait is what has to stay bounded.
        const deadline = Date.now() + maxAttempts * intervalMs;

        for (let i = 0; i < maxAttempts && Date.now() < deadline; i++) {
            // Check SDK signal first — fires immediately when user cancels 3DS or SDK reports result.
            // This avoids waiting for the backend when the outcome is already known.
            if (card3dsSdkResult) {
                var sdkResult = card3dsSdkResult;
                card3dsSdkResult = null;
                var sdkStatus = sdkResult.status;
                if (sdkStatus === 'SUCCEEDED' || sdkStatus === 'APPROVED') return 'SUCCEEDED';
                if (sdkStatus === 'CANCELED') return 'CANCELED';
                return 'ERROR';
            }

            // Use short-step sleep so SDK signal is detected within ~200ms instead of waiting the full interval.
            await sleepWatchingSdkSignal(intervalMs);

            // Re-check after sleep — skip backend call if SDK already signalled.
            if (card3dsSdkResult) continue;

            try {
                // Below Cloudflare's 100 s origin cut-off, so a reply the edge would still deliver is never dropped; above the server's 30 s Yuno budget.
                const resp = await fetch(yunoStatusUrl, {
                    method: 'POST',
                    signal: requestTimeout(90000),
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'X-CSRF-TOKEN': getCsrfToken(),
                    },
                    body: JSON.stringify({ charge_id: chargeId }),
                });

                const data = await resp.json();
                transportFailures = 0;

                // The backend could not answer about a charge that may still settle: not the same as a decline.
                if (data.success === false) {
                    return 'UNCONFIRMED';
                }
                if (data.success && data.status) {
                    const s = data.status;
                    if (s === 'REJECTED' || s === 'EXPIRED') {
                        return 'DECLINED';
                    }
                    if (s === 'SUCCEEDED' || s === 'DECLINED' || s === 'ERROR' || s === 'CANCELED') {
                        return s;
                    }
                }
            } catch (err) {
                // One lost poll must not fail a challenge the customer may be completing right now.
                console.warn('[YUNO] Status poll error:', err);
                if (++transportFailures >= 3) {
                    return 'UNCONFIRMED';
                }
            }
        }

        return 'TIMEOUT';
    }

    async function finalizePayment(chargeId, originalPayload) {
        const payload = Object.assign({}, originalPayload, { charge_id: chargeId });

        const resp = await fetch(yunoFinalizeUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            body: JSON.stringify(payload),
        });

        let result;
        try {
            result = await safeParseJson(resp);
        } catch (err) {
            hideSpinner();
            disablePayButton(false);
            showPaymentError(err.message);
            recreateCheckoutSession();
            return;
        }

        if (!result.success) {
            hideSpinner();
            disablePayButton(false);
            if (result.hasTrial) {
                handleTrialError(result.message);
            } else {
                showPaymentError(result.message || 'Payment finalization failed.', !!result.pending);
            }
            recreateCheckoutSession();
            return;
        }

        if (result.redirect) {
            await saveOrderData(result.id);
            window.location.href = result.redirect;
        }
    }

    // The trial is sold once per account and was refused, before any charge. The page leaves the trial for
    // the packs, with none chosen — what to pay instead is the buyer's pick — and says why right above them.
    function handleTrialError(message) {
        const errorEl = document.getElementById('packets-error');
        if (!errorEl) {
            showPaymentError(message);
            return;
        }
        // The payment page has no packs to point to and brings its own text.
        if (errorEl.hasAttribute('data-own-text')) {
            errorEl.hidden = false;
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        // The page's own "View all our packages": packs on show, nothing chosen, their consent text.
        const viewPacksBtn = document.querySelector('.precheck__view-packs');
        if (viewPacksBtn) viewPacksBtn.click();
        const viewOfferBtn = document.querySelector('.precheck__view-offer');
        if (viewOfferBtn) viewOfferBtn.style.display = 'none';

        errorEl.textContent = message || '';
        errorEl.hidden = false;
        selectPacketAlert();
    }

    // -------------------------------------------------------------------------
    // Session recreation after payment failure
    // -------------------------------------------------------------------------
    async function recreateCheckoutSession() {
        try {
            const packetId = getPacketId();
            const newSession = await fetchCheckoutSession(packetId);
            if (newSession) {
                checkoutSessionId = newSession;
                setCheckoutSessionInput(newSession);
                console.log('[YUNO] Checkout session recreated');
            }
        } catch (err) {
            console.warn('[YUNO] Failed to recreate checkout session:', err);
        }
    }

    // -------------------------------------------------------------------------
    // Wallet payments (Google Pay / Apple Pay)
    // -------------------------------------------------------------------------

    function setWalletButtonsLoading(loading) {
        var targets = document.querySelectorAll('#google-pay-button, #apple-pay-proxy');
        targets.forEach(function (el) {
            el.classList.toggle('wallet-loading', loading);
            if (el.tagName === 'BUTTON') el.disabled = loading;
        });
    }

    async function initWalletButtons() {
        if (walletInitialized || !yunoInstance || !checkoutSessionId) return;

        walletInitialized = true;
        walletSessionReady = false;

        var myId = ++walletRemountRequestId;
        setWalletButtonsLoading(true);
        try {
            await mountWalletCheckout();
            walletSessionReady = true;
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[YUNO][wallet] Init error:', err);
        } finally {
            if (myId === walletRemountRequestId) setWalletButtonsLoading(false);
        }
    }

    async function onPaymentResult(status, subStatus) {
        var isCanceled = status === 'PENDING' && subStatus === 'CANCELLED_BY_USER';
        console.log('[YUNO] yunoPaymentResult:', status, subStatus);

        if (card3dsPollingActive) {
            // Only a cancel or a success is final here; PENDING/AUTHORIZED on a trial is the backend poll's call.
            var terminal = isCanceled || status === 'SUCCEEDED' || status === 'APPROVED';
            if (terminal) {
                card3dsSdkResult = { status: isCanceled ? 'CANCELED' : status };
            }
            return;
        }

        if (isCanceled) {
            pendingWalletChargeId = null;
            pendingWalletResult = null;
            hideSpinner();
            disablePayButton(false);
            remountWalletInBackground();
            return;
        }

        if (pendingWalletChargeId === null) {
            pendingWalletResult = status;
            return;
        }

        await processWalletSdkResult();
    }

    async function startWalletCheckout() {
        var countryForCheckout = (typeof yunoCountryCode !== 'undefined' ? yunoCountryCode : 'US');

        await yunoInstance.startCheckout({
            checkoutSession: checkoutSessionId,
            elementSelector: '#yuno-checkout-mount',
            countryCode: countryForCheckout,
            language: 'en',
            showLoading: false,
            showPaymentStatus: false,
            // Google's guidelines want the button no smaller than the page's other buttons and forbid restyling its corners, so only the height is set.
            externalButtons: { googlePay: { buttonHeight: 60 } },

            yunoCreatePayment: async function (oneTimeToken) {
                await handleWalletPayment(oneTimeToken);
            },

            yunoPaymentResult: onPaymentResult,

            yunoError: function (message, data) {
                console.error('[YUNO][wallet] yunoError:', message, data);

                if (card3dsPollingActive) {
                    var code = (data && (data.code || data.status)) || message || '';
                    var isCanceled = /CANCEL{1,2}ED_BY_USER/.test(String(code)) || /CANCEL{1,2}ED_BY_USER/.test(String(message));
                    card3dsSdkResult = { status: isCanceled ? 'CANCELED' : 'ERROR', message: message || 'Payment failed' };
                    return;
                }

                pendingWalletChargeId = null;
                pendingWalletResult = null;
                hideSpinner();
                disablePayButton(false);

                var errorCode = (data && (data.code || data.status)) || message || '';
                var canceled = /CANCEL{1,2}ED_BY_USER/.test(String(errorCode)) || /CANCEL{1,2}ED_BY_USER/.test(String(message));

                if (!canceled) {
                    showPaymentError(message || 'Payment failed');
                }

                remountWalletInBackground();
            },
        });
    }

    async function mountWalletButtons() {
        var safari = isSafariBrowser();
        var buttons = [
            { paymentMethodType: 'GOOGLE_PAY', elementSelector: '#google-pay-button' },
        ];
        // No container means the option is off server-side (config('services.apple_pay.enabled')),
        // and then the SDK must not be asked to set the wallet up at all.
        if (safari && document.getElementById('apple-pay-button')) {
            buttons.push({ paymentMethodType: 'APPLE_PAY', elementSelector: '#apple-pay-button' });
        }

        try {
            await yunoInstance.mountExternalButtons(buttons);
            // Device availability cannot change between remounts, so re-deciding it can only produce false negatives.
            if (!walletAvailabilityChecked) {
                walletAvailabilityChecked = true;
                hideUnavailableWallets(buttons);
            }
        } catch (e) {
            console.warn('[YUNO][wallet] mountExternalButtons error:', e.message, e);
        }
    }

    // The SDK renders nothing into the container when the wallet is unavailable on this device
    // (Apple Pay not set up in Safari, Google Pay isReadyToPay=false), so the tab must go with it.
    function hideUnavailableWallets(buttons) {
        buttons.forEach(function (button) {
            var container = document.querySelector(button.elementSelector);
            if (!container || container.querySelector('button, [role="button"], apple-pay-button')) return;

            var payType = button.paymentMethodType === 'APPLE_PAY' ? 'apple' : 'google';
            document.querySelectorAll('.opt-' + payType).forEach(function (el) { el.classList.add('d-none'); });
            var payTypeInput = document.getElementById('pay-type');
            var cardTab = document.querySelector('.p-type.p-card');
            if (payTypeInput && payTypeInput.value === payType && cardTab) {
                handlePaymentMethodSwitch('cc', 'cc-pay', cardTab);
            }
        });
    }

    async function mountWalletCheckout() {
        await startWalletCheckout();
        await mountWalletButtons();
    }

    async function remountWalletInBackground() {
        var packetId = getPacketId();
        if (!packetId) return;

        walletSessionReady = false;
        var myId = ++walletRemountRequestId;
        setWalletButtonsLoading(true);
        try {
            await recreateCheckoutSession();
            await startWalletCheckout();
            await mountWalletButtons();
            walletSessionReady = true;
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.warn('[YUNO][wallet] Re-mount error:', err.message);
        } finally {
            if (myId === walletRemountRequestId) setWalletButtonsLoading(false);
        }
    }

    async function switchSessionOnPacketChange(packetId) {
        if (!yunoInstance) return;

        const needsWalletRemount = walletInitialized;

        if (needsWalletRemount) {
            walletSessionReady = false;
        }
        var myId = ++walletRemountRequestId;
        if (needsWalletRemount) {
            setWalletButtonsLoading(true);
        }

        try {
            // Always recreate the checkout session for the new packet — PATCH update
            // is not picked up by Apple Pay / Google Pay sheets, so we mirror epicvin
            // behaviour and request a fresh session per packet.
            const newSession = await fetchCheckoutSession(packetId);
            if (myId !== walletRemountRequestId) return;
            if (newSession) {
                checkoutSessionId = newSession;
                setCheckoutSessionInput(newSession);
            }

            if (!needsWalletRemount) return;

            await startWalletCheckout();
            await mountWalletButtons();
            walletSessionReady = true;
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.warn('[YUNO][wallet] Re-mount on packet change error:', err.message);
        } finally {
            if (needsWalletRemount && myId === walletRemountRequestId) {
                setWalletButtonsLoading(false);
            }
        }
    }

    function validateWalletData() {
        if (!getPacketId()) {
            selectPacketAlert();
            return false;
        }

        if (!validateEmailField()) {
            document.getElementById('email')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }

        var agree = document.getElementById('agree');
        var agreeFirst = document.getElementById('agreeFirst');
        var agreeSecond = document.getElementById('agreeSecond');

        var valid = true;
        var firstInvalid = null;

        if (agree && agree.closest('[style*="display: none"]') === null && !agree.checked) {
            agree.classList.add('is-invalid');
            valid = false;
            firstInvalid = firstInvalid || agree;
        }

        if (agreeFirst && agreeFirst.offsetParent !== null && !agreeFirst.checked) {
            agreeFirst.classList.add('is-invalid');
            valid = false;
            firstInvalid = firstInvalid || agreeFirst;
        }

        if (agreeSecond && agreeSecond.offsetParent !== null && !agreeSecond.checked) {
            agreeSecond.classList.add('is-invalid');
            valid = false;
            firstInvalid = firstInvalid || agreeSecond;
        }

        if (!valid && firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return valid;
    }

    // Google's button belongs to Google, so the pre-payment checks run in the capture phase
    // on its container — stopping the click there is the only way to hold the sheet back.
    function bindGooglePayGuard() {
        var container = document.getElementById('google-pay-button');
        if (!container) return;

        container.addEventListener('click', function (e) {
            if (window.onDh) {
                e.preventDefault();
                e.stopPropagation();
                showDisputeModal();
                return;
            }

            card3dsPollingActive = false;
            card3dsSdkResult = null;

            if (!walletSessionReady || !validateWalletData()) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            track('add_payment_info', paymentParams('google'));
            pendingWalletChargeId = null;
            pendingWalletResult = null;
            showSpinner();
            disablePayButton(true);
        }, true);
    }

    function handleWalletProxyClick(sdkContainerId) {
        if (window.onDh) {
            showDisputeModal();
            return;
        }

        card3dsPollingActive = false;
        card3dsSdkResult = null;

        if (!walletSessionReady) return;

        if (!validateWalletData()) return;

        track('add_payment_info', paymentParams(sdkContainerId === 'apple-pay-button' ? 'apple' : 'google'));
        pendingWalletChargeId = null;
        pendingWalletResult = null;
        showSpinner();
        disablePayButton(true);

        var container = document.getElementById(sdkContainerId);
        var sdkButton = container && container.querySelector('button, [role="button"], apple-pay-button');

        if (sdkButton) {
            sdkButton.click();
        } else {
            hideSpinner();
            disablePayButton(false);
            console.error('[YUNO][wallet] SDK button not found in #' + sdkContainerId);
            showPaymentError('Payment method not available. Please reload the page.');
        }
    }

    async function finalizeWalletCharge(chargeId) {
        var MAX_ATTEMPTS = 8;
        var RETRY_DELAY_MS = 3000;

        var payType = document.getElementById('pay-type') ? document.getElementById('pay-type').value : 'google';
        var formParams = new URLSearchParams();
        formParams.set('charge_id', chargeId);
        formParams.set('packet', getPacketId() || '');
        formParams.set('email', getEmailValue() || '');
        formParams.set('vin', document.getElementById('pay-vin') ? document.getElementById('pay-vin').value : '');
        formParams.set('vehicle', document.getElementById('pay-vehicle') ? document.getElementById('pay-vehicle').value : '');
        formParams.set('variant', document.querySelector('[name="variant"]') ? document.querySelector('[name="variant"]').value : '');
        formParams.set('paymentType', payType);
        formParams.set('browser_info', JSON.stringify(browserInfo));
        formParams.set('page-uuid', document.getElementById('page-uuid') ? document.getElementById('page-uuid').value : '');
        formParams.set('client-data', JSON.stringify(collectBrowserData()));
        formParams.set('agree-data', JSON.stringify(getAgreeData()));
        var utmParamsWallet = getUtmParams();
        if (utmParamsWallet !== null) {
            formParams.set('utm_params', utmParamsWallet);
        }

        for (var attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            var resp = await fetch(yunoFinalizeUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                body: formParams.toString(),
            });

            var payload = null;
            try {
                payload = await resp.json();
            } catch (e) {
                showPaymentError('Payment error. Please try again.');
                remountWalletInBackground();
                return;
            }

            if (payload.retriable) {
                if (attempt < MAX_ATTEMPTS) {
                    await sleep(RETRY_DELAY_MS);
                    continue;
                }
                showPaymentError(payload.message || 'Payment is still being processed. Please wait and try again.', true);
                remountWalletInBackground();
                return;
            }

            return handleWalletResult(payload);
        }
    }

    async function processWalletSdkResult() {
        if (pendingWalletChargeId === null) return;

        var chargeId = pendingWalletChargeId;
        pendingWalletChargeId = null;
        pendingWalletResult = null;

        var redirected = false;
        try {
            await sleep(3000);
            redirected = await finalizeWalletCharge(chargeId);
        } catch (err) {
            console.error('[YUNO][wallet] Finalize error:', err);
            showPaymentError(err.message || 'Payment was not completed.');
            remountWalletInBackground();
        } finally {
            if (!redirected) {
                hideSpinner();
                disablePayButton(false);
            }
        }
    }

    async function handleWalletPayment(oneTimeToken) {
        var waitForWalletResult = false;
        var redirected = false;
        showSpinner();
        disablePayButton(true);

        // Start screenshot capture in background while waiting for payment API
        generateScreenShot().catch(function () {});

        try {
            var payType = document.getElementById('pay-type') ? document.getElementById('pay-type').value : 'google';
            var resp = await fetch(yunoPaymentUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                body: JSON.stringify({
                    token: oneTimeToken,
                    checkout_session: checkoutSessionId,
                    packet: getPacketId(),
                    email: getEmailValue(),
                    vin: document.getElementById('pay-vin') ? document.getElementById('pay-vin').value : '',
                    vehicle: document.getElementById('pay-vehicle') ? document.getElementById('pay-vehicle').value : '',
                    variant: document.querySelector('[name="variant"]') ? document.querySelector('[name="variant"]').value : '',
                    paymentType: payType,
                    browser_info: JSON.stringify(browserInfo),
                    'page-uuid': document.getElementById('page-uuid') ? document.getElementById('page-uuid').value : '',
                    'client-data': JSON.stringify(collectBrowserData()),
                    'agree-data': JSON.stringify(getAgreeData()),
                    utm_params: getUtmParams(),
                }),
            });

            var payload;
            try {
                payload = await safeParseJson(resp);
            } catch (err) {
                showPaymentError(err.message);
                remountWalletInBackground();
                return;
            }

            if (payload.sdk_action_required && payload.charge_id && yunoInstance) {
                pendingWalletChargeId = payload.charge_id;
                waitForWalletResult = true;

                if (pendingWalletResult !== null) {
                    await processWalletSdkResult();
                    return;
                }

                yunoInstance.continuePayment({ showPaymentStatus: false, showLoading: false }).catch(function (err) {
                    console.warn('[YUNO][wallet] continuePayment rejected:', err && err.message);
                    // The SDK settles this promise as a rejection after the sheet closes, so acting on it
                    // unguarded reports a failure over a payment the result callback is already finalizing.
                    if (pendingWalletChargeId === null) return;
                    pendingWalletChargeId = null;
                    pendingWalletResult = null;
                    hideSpinner();
                    disablePayButton(false);
                    showPaymentError('Payment failed. Please try again.');
                    remountWalletInBackground();
                });
                return;
            }

            redirected = await handleWalletResult(payload);
            if (!payload.redirect) {
                remountWalletInBackground();
            }
        } catch (e) {
            console.error('[YUNO][wallet] Payment error:', e);
            showPaymentError('Payment failed. Please try again.');
            remountWalletInBackground();
        } finally {
            if (!waitForWalletResult && !redirected) {
                hideSpinner();
                disablePayButton(false);
            }
        }
    }

    // Returns true once it has sent the browser to the success page; the callers' finally blocks then
    // leave the page locked until it goes.
    async function handleWalletResult(payload) {
        if (!payload.success) {
            hideSpinner();
            disablePayButton(false);
            if (payload.hasTrial) {
                handleTrialError(payload.message);
            } else if (!showServerEmailError(payload)) {
                showPaymentError(payload.message || 'Payment failed. Please try again.', !!payload.pending);
            }
            return false;
        }

        if (payload.redirect) {
            await saveOrderData(payload.id);
            window.location.href = payload.redirect;
            return true;
        }
        return false;
    }

    // -------------------------------------------------------------------------
    // Screenshot & order data saving
    // -------------------------------------------------------------------------

    function blobToBase64(blob) {
        return new Promise(function (resolve, reject) {
            var reader = new FileReader();
            reader.onloadend = function () {
                var result = reader.result;
                if (typeof result === 'string') {
                    resolve(result.split(',')[1] || null);
                } else {
                    resolve(null);
                }
            };
            reader.onerror = function () { reject(new Error('FileReader error')); };
            reader.readAsDataURL(blob);
        });
    }

    async function waitForScreenshot(timeout) {
        timeout = timeout || 8000;
        var start = Date.now();
        while (Date.now() - start < timeout) {
            if (window.screenShot && (window.screenShot.image || window.screenShot.error)) {
                return Date.now() - start;
            }
            await sleep(200);
        }
        return null;
    }

    async function buildScreenshotJson(hash, paymentType) {
        var payType = paymentType || (document.getElementById('pay-type') ? document.getElementById('pay-type').value : '');
        var tm = Date.now();
        var payload = { hash: hash, paymentType: payType, tm: tm };

        try {
            if (window.screenShot && window.screenShot.image) {
                var base64 = await blobToBase64(window.screenShot.image);
                if (base64) {
                    var ext = (window.screenShot.image && window.screenShot.image.type === 'image/webp') ? 'webp' : 'jpg';
                    payload.image = { filename: 'image.' + ext, base64: base64 };
                }
            }
            if (window.screenShot && window.screenShot.error) {
                payload.error = window.screenShot.error;
            }
        } catch (e) {
            console.warn('[screenshot] buildScreenshotJson failed:', e);
        }

        return payload;
    }

    async function saveOrderData(hash, paymentType) {
        if (!hash || typeof saveScreenUrl === 'undefined' || !saveScreenUrl) {
            return;
        }
        var screenPayload = null;
        try {
            await waitForScreenshot(8000);
            screenPayload = await buildScreenshotJson(hash, paymentType);

            await fetch(saveScreenUrl, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-TOKEN': getCsrfToken(),
                },
                body: JSON.stringify(screenPayload),
            });
        } catch (e) {
            console.error('[screenshot] saveOrderData failed:', e);
        } finally {
            if (screenPayload && screenPayload.image) {
                screenPayload.image.base64 = null;
                screenPayload.image = null;
            }
            cleanupScreenShot();
        }
    }

    // -------------------------------------------------------------------------
    // Utilities
    // -------------------------------------------------------------------------
    function sleep(ms) {
        return new Promise(function (resolve) { return setTimeout(resolve, ms); });
    }

    function isSafariBrowser() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    // -------------------------------------------------------------------------
    // Packet selection helpers
    // -------------------------------------------------------------------------
    function selectPacketAlert() {
        const packetsSection = document.querySelector('.precheck__packets');
        if (packetsSection) {
            packetsSection.scrollIntoView({ behavior: 'smooth' });
        }

        const packets = document.querySelector('.precheck__packets .packets');
        if (packets) {
            packets.classList.add('is-invalid');
            setTimeout(function () {
                packets.classList.remove('is-invalid');
            }, 3000);
        }
    }

    function getPacketAmount(id) {
        if (typeof packets === 'undefined' || !Array.isArray(packets)) {
            return null;
        }
        const packet = packets.find(function (p) { return String(p.id) === String(id); });
        if (!packet) return null;
        return packet.trial_amount ? packet.trial_amount : packet.amount;
    }

    function setTotalText(id) {
        const amount = getPacketAmount(id);
        const text = amount !== null ? '$' + amount : '';
        document.querySelectorAll('.tot-val').forEach(function (el) { el.textContent = text; });
    }

    // -------------------------------------------------------------------------
    // Payment method tab switching
    // -------------------------------------------------------------------------
    function handlePaymentMethodSwitch(payType, payBtnClass, activeTabBtn) {
        const payTypeInput = document.getElementById('pay-type');
        if (payTypeInput && payTypeInput.value === payType) return;

        // Cancel any active 3DS flow before switching
        card3dsPollingActive = false;
        card3dsSdkResult = null;

        // Switch active tab
        document.querySelectorAll('.p-type').forEach(function (b) { b.classList.remove('active'); });
        activeTabBtn.classList.add('active');

        if (payTypeInput) payTypeInput.value = payType;

        // Show only the relevant pay button
        const payButtonWrapper = document.getElementById('pay-btn');
        if (payButtonWrapper) {
            payButtonWrapper.querySelectorAll('.pay-btn').forEach(function (b) { b.style.display = 'none'; });
            const target = payButtonWrapper.querySelector('.' + payBtnClass);
            if (target) target.style.display = payType === 'cc' ? '' : 'block';
        }

        // Show/hide card fields
        const cardData = document.getElementById('card-data');
        if (cardData) cardData.style.display = payType === 'cc' ? '' : 'none';

        // Initialize wallet buttons on first click of Google/Apple Pay tab
        if (payType === 'google' || payType === 'apple') {
            void initWalletButtons();
        }
    }

    // -------------------------------------------------------------------------
    // Event binding
    // -------------------------------------------------------------------------
    function bindEvents() {
        // All pay buttons — delegated from document so it works across all page layouts.
        // Handles card, PayPal and wallet proxy buttons in one place.
        document.addEventListener('click', function (e) {
            let btn = e.target.closest('#pay-btn .pay-btn');

            // Fallback for programmatic clicks on #pay-btn itself (e.g. tests)
            if (!btn && e.target.id === 'pay-btn') {
                btn = e.target.querySelector('.pay-btn:not([style*="display: none"])');
            }

            if (!btn) return;

            // The Google Pay container holds Google's own button; its click must reach it untouched.
            if (btn.id === 'google-pay-button') return;

            e.preventDefault();

            if (btn.classList.contains('cc-pay')) {
                submitYunoPayment();
            } else if (btn.classList.contains('pp-pay')) {
                if (!getPacketId()) {
                    selectPacketAlert();
                    return;
                }
                if (!validatePaypalForm()) {
                    scrollToFirstError();
                    return;
                }
                track('add_payment_info', paymentParams('pp'));
                submitPaypalPayment();
            } else if (btn.id === 'apple-pay-proxy') {
                handleWalletProxyClick('apple-pay-button');
            }
        });

        // Payment method tabs
        document.querySelectorAll('.p-type').forEach(function (btn) {
            btn.addEventListener('click', function () {
                let payType = 'cc';
                let payBtnClass = 'cc-pay';

                if (btn.classList.contains('p-pp')) {
                    payType = 'pp';
                    payBtnClass = 'pp-pay';
                } else if (btn.classList.contains('p-google')) {
                    payType = 'google';
                    payBtnClass = 'gp-pay';
                } else if (btn.classList.contains('p-apple')) {
                    payType = 'apple';
                    payBtnClass = 'ap-pay';
                }

                handlePaymentMethodSwitch(payType, payBtnClass, btn);
            });
        });

        // Packet selection — mirrors checkout.js / checkout-old.js .pack click handler
        document.addEventListener('click', function (e) {
            const btn = e.target.closest('.pack');
            if (!btn) return;
            if (btn.classList.contains('active')) return;

            document.querySelectorAll('.pack').forEach(function (el) { el.classList.remove('active'); });
            btn.classList.add('active');

            const id = btn.dataset.id;
            const packetIdEl = document.getElementById('packet-id');
            if (packetIdEl) packetIdEl.value = id;

            setTotalText(id);
            syncFundsButtonState();
            track('select_plan', planParams(id));
            // A new checkout session is created inside switchSessionOnPacketChange
            // so wallets (Apple/Google Pay) always show the new packet price.
            switchSessionOnPacketChange(id);

            // Reveal sidebar payment panel (packets/not-found page)
            const appEl = document.getElementById('app');
            if (appEl && appEl.classList.contains('select-packets-none')) {
                appEl.classList.remove('select-packets-none');
                appEl.classList.add('checkout');
            }
            const payBlock = document.getElementById('pay-block');
            if (payBlock) {
                payBlock.style.display = 'block';
                // Render Yuno secure fields now that sidebar is visible
                maybeRenderSecureFields();
                if (window.innerWidth < 992) {
                    payBlock.scrollIntoView({ behavior: 'smooth' });
                }
            }
        });

        // Packet change via custom event (fired by "View all packages" / "Return to special offer")
        document.addEventListener('change:packet', function (e) {
            const id = e.detail && e.detail.id;
            const packetIdEl = document.getElementById('packet-id');

            if (id) {
                if (packetIdEl) packetIdEl.value = id;
                setTotalText(id);
                track('select_plan', planParams(id));
                // Recreate the checkout session and remount wallets so Apple/Google Pay
                // sheets reflect the new packet price (PATCH does not work for wallets).
                switchSessionOnPacketChange(id);
            } else {
                // "View all packages" — no packet selected, clear it
                if (packetIdEl) packetIdEl.value = '';
                document.querySelectorAll('.pack').forEach(function (el) {
                    el.classList.remove('active');
                });
                setTotalText('');
            }

            syncFundsButtonState();
        });

        // Email field — an error goes as soon as the address is right; a new one waits for blur or submit
        const emailEl = document.getElementById('email');
        if (emailEl) {
            emailEl.addEventListener('input', function () {
                if (emailEl.classList.contains('is-invalid') && validateEmail(emailEl.value)) {
                    clearFieldError('email');
                }
            });
            emailEl.addEventListener('change', function () {
                if (getEmailValue()) validateEmailField();
            });
        }

        // Name on card field — letters, spaces and hyphens only
        const ccNameEl = document.getElementById('cc-name');
        if (ccNameEl) {
            ccNameEl.addEventListener('keypress', function (e) {
                const char = String.fromCharCode(e.charCode || e.keyCode);
                if (!/[a-zA-ZÀ-ÖØ-öø-ÿ\s\-]/.test(char)) {
                    e.preventDefault();
                }
            });

            ccNameEl.addEventListener('input', function () {
                // Strip any invalid characters that could arrive via paste
                const cleaned = ccNameEl.value.replace(/[^a-zA-ZÀ-ÖØ-öø-ÿ\s\-]/g, '');
                if (cleaned !== ccNameEl.value) {
                    const pos = ccNameEl.selectionStart - (ccNameEl.value.length - cleaned.length);
                    ccNameEl.value = cleaned;
                    ccNameEl.setSelectionRange(pos, pos);
                }

                if (ccNameEl.value.trim()) {
                    clearFieldError('cc-name');
                    ccNameEl.classList.remove('is-invalid');
                }
            });
        }

        // Agree checkbox — clear error when checked
        document.addEventListener('change', function (e) {
            const el = e.target;
            if (el && (el.id === 'agree' || el.id === 'agreeFirst' || el.id === 'agreeSecond')) {
                if (el.checked) {
                    el.classList.remove('is-invalid');
                }
            }
        });

        // Back from PayPal, or from wherever a payment led, the browser may restore this page from
        // its back/forward cache exactly as it was left: locked, with the buttons disabled.
        window.addEventListener('pageshow', function (e) {
            if (e.persisted) {
                hideSpinner();
                disablePayButton(false);
            }
        });

        // "Try again to pay" button on main precheck page — mirrors showPay() from checkout-old.js
        document.addEventListener('click', function (e) {
            if (e.target.closest('#back-pay')) {
                const payFail = document.getElementById('pay-fail');
                const payIn = document.querySelector('.pay-in');
                const payBtn = document.querySelector('#pay-block #pay-btn');

                if (payFail && payIn) {
                    payFail.style.display = 'none';
                    if (payBtn) payBtn.style.display = '';
                    payIn.style.display = payInPrevDisplay || 'flex';
                    payIn.style.opacity = '0';
                    payIn.style.transition = 'opacity 0.5s';
                    void payIn.offsetHeight;
                    payIn.style.opacity = '1';
                    setTimeout(function () {
                        payIn.style.transition = '';
                    }, 500);
                }

                disablePayButton(false);
            }
        });

        // Use prepaid reports button (.use-prepaid)
        document.addEventListener('click', function (e) {
            const btn = e.target.closest('.use-prepaid');
            if (!btn) return;

            e.preventDefault();
            window.prepaidType = btn.dataset.type || '';

            usePrepaidBalance();
        });

        // Use free balance / prepaid funds button (.use-funds)
        document.addEventListener('click', function (e) {
            const btn = e.target.closest('.use-funds');
            if (!btn) return;

            if (btn.classList.contains('disabled')) return;

            e.preventDefault();

            const packetId = getPacketId();
            if (!packetId) return;

            useFunds();
        });

        // Trial-used modal "Continue" button — emulate click on the unlim packet
        document.addEventListener('click', function (e) {
            if (!e.target.closest('#used-modal .precheck-modal__button')) return;

            const unlimEl = document.querySelector('.packet-unlim');
            if (unlimEl) unlimEl.click();
        });


        // Sync .use-funds disabled state with packet selection
        syncFundsButtonState();

    }

    function syncFundsButtonState() {
        const packetId = getPacketId();
        const hasPacket = !!packetId;
        const amount = hasPacket ? getPacketAmount(packetId) : null;
        const enough = amount !== null && parseFloat(amount) <= (parseFloat(window.userBalance) || 0);
        document.querySelectorAll('.use-funds').forEach(function (btn) {
            btn.style.display = hasPacket ? '' : 'none';
        });
        document.querySelectorAll('.prepaid-txt__use').forEach(function (btn) {
            btn.classList.toggle('active', amount !== null && enough);
        });
        document.querySelectorAll('.prepaid-txt__none').forEach(function (btn) {
            btn.classList.toggle('active', amount !== null && !enough);
        });
    }

    function useFunds() {
        var fundsUrl = window.fundsUrl;
        if (!fundsUrl) {
            console.error('[YUNO] fundsUrl not defined');
            return;
        }

        showSpinner();
        disablePayButton(true);
        hidePaymentError();

        var form = document.getElementById('payment-form');
        var formData = new FormData(form || undefined);

        var params = new URLSearchParams();
        formData.forEach(function (value, key) { params.append(key, value); });

        fetch(fundsUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            body: params.toString(),
        })
            .then(function (resp) { return resp.json(); })
            .then(function (result) {
                if (result.redirect) {
                    window.location.href = result.redirect;
                } else {
                    hideSpinner();
                    disablePayButton(false);
                    showPaymentError(result.message || 'Payment failed. Please try again.');
                }
            })
            .catch(function () {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('Payment failed. Please try again.');
            });
    }

    function usePrepaidBalance() {
        var prepaidUrl = window.prepaidUrl;
        if (!prepaidUrl) {
            console.error('[YUNO] prepaidUrl not defined');
            return;
        }

        showSpinner();
        disablePayButton(true);
        hidePaymentError();

        var form = document.getElementById('payment-form');
        var formData = new FormData(form || undefined);
        formData.set('type', window.prepaidType || '');

        var params = new URLSearchParams();
        formData.forEach(function (value, key) { params.append(key, value); });

        fetch(prepaidUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
            },
            body: params.toString(),
        })
            .then(function (resp) { return resp.json(); })
            .then(function (result) {
                if (result.redirect) {
                    window.location.href = result.redirect;
                } else {
                    hideSpinner();
                    disablePayButton(false);
                    showPaymentError(result.message || 'Payment failed. Please try again.');
                }
            })
            .catch(function () {
                hideSpinner();
                disablePayButton(false);
                showPaymentError('Payment failed. Please try again.');
            });
    }

    // -------------------------------------------------------------------------
    // Boot
    // -------------------------------------------------------------------------
    function showWalletTabs() {
        if (isSafariBrowser()) {
            document.querySelectorAll('.opt-apple').forEach(function (el) { el.classList.remove('d-none'); });
        } else {
            document.querySelectorAll('.opt-google').forEach(function (el) { el.classList.remove('d-none'); });
        }
    }

    function boot() {
        showWalletTabs();
        bindEvents();
        bindGooglePayGuard();
        initializeZipCodeValidation('cc-zip');

        // Init Yuno when SDK script is loaded
        if (typeof Yuno !== 'undefined') {
            initYuno();
        } else {
            // Wait for Yuno SDK to be available
            const maxWait = 30;
            let waited = 0;
            const interval = setInterval(function () {
                waited++;
                if (typeof Yuno !== 'undefined') {
                    clearInterval(interval);
                    initYuno();
                } else if (waited >= maxWait) {
                    clearInterval(interval);
                    console.error('[YUNO] SDK not loaded after ' + maxWait + ' seconds');
                }
            }, 1000);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }
})();
